import { useFormikEnhanced } from '@superdispatch/forms';
import { useSnackbarStack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { APIError } from 'shared/api/APIError';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { useInitialValues } from 'shared/form/AppFormik';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { useFlaggedSchema } from 'shared/hooks/useFlaggedSchema';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { ProfileW9Form } from 'shared/modules/carrier-profile/core/ProfileW9Form';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';
import {
  useCarrierProfileAPI,
  useW9,
} from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import {
  W9DTO,
  w9Schema,
  w9YupSchema,
} from 'shared/modules/carrier-profile/data/CarrierProfileDTO';
import {
  useSubmitVerificationDocument,
  VerificationDialogs,
} from './core/VerificationDialogs';
import { W9Preview } from './core/W9Preview';
import { ProfilePageLayout } from './ProfilePageLayout';
import { useEffect, useMemo } from 'react';

export function ProfileW9Page() {
  const { addSnackbar } = useSnackbarStack();
  const { data, isLoading, isError } = useW9();
  const { updateW9 } = useCarrierProfileAPI();
  const schema = useFlaggedSchema('ctms_zod_schema', w9YupSchema, w9Schema);
  const initialValues = useInitialValues(schema, data);
  const validationSchema = useMemo(
    () => toFormikValidationSchema(schema),
    [schema],
  );

  const formik = useFormikEnhanced<W9DTO, APIResponse>({
    initialValues,
    validationSchema,
    onSubmit: updateW9,
    onSubmitSuccess: () => {
      addSnackbar('W-9 Form was successfully updated', { variant: 'success' });

      trackCarrierProfileEvent({
        name: 'Carrier Updated Carrier Info',
        utm_medium: 'Carrier Profile',
        utm_content: 'W-9 Form',
      });
    },
    onSubmitFailure: ({ message }: APIError) => {
      addSnackbar(`Failed to update W-9 form: ${message}`, {
        variant: 'error',
      });
    },
  });

  const submitVerificationForm = useSubmitVerificationDocument(formik);

  useEffect(() => {
    if (isError) {
      addSnackbar('Failed to fetch W-9 Form', { variant: 'error' });
    }
  }, [isError, addSnackbar]);

  return (
    <>
      <DocumentTitle title="W-9 Form - Profile" />

      <VerificationDialogs formik={formik} />

      <ProfilePageLayout
        formik={formik}
        title="W-9 Form"
        size={isWebView ? 'small' : 'medium'}
        isLoading={isLoading}
        actions={
          <Button
            type="button"
            onClick={submitVerificationForm}
            pending={formik.isSubmitting}
            disabled={!formik.dirty}
            fullWidth={isWebView}
          >
            Save
          </Button>
        }
        extraDetails={<W9Preview />}
      >
        <ProfileW9Form />
      </ProfilePageLayout>
    </>
  );
}
